<template>
	<Modal
		v-if="visible"
		@modal:close="handleCloseModal"
	>
		<template #body>
			<div class="flex flex-col items-center">
				<img
					:src="CoverLetterAiLogo"
					class="w-[263px]"
					title="CoverLetter AI"
				/>
				<div class="mt-6 font-[Inter] text-2xl font-bold text-[#323232]">Welcome to CoverLetterAI!</div>
				<div class="mx-2 mt-6 font-[Inter] text-sm font-normal text-[#323232]">
					Create your AI powered cover letter so you can impress recruiters with a professional introduction.
				</div>
			</div>
		</template>

		<template #actions>
			<won-button
				class="mt-2.5 w-full justify-center font-[Roboto] font-medium"
				@click="handleStartOnboarding"
			>Begin</won-button>
		</template>
	</Modal>
</template>

<script setup>
import introJS from 'intro.js';
import { ref } from 'vue';

import Modal from '../components/modal';
import WonButton from '../components/won-button';


import { introJsButton } from '../utils/styles';
import { coverletterOnboarding } from '../utils/onboarding';
import { delay } from '../utils/helpers';

import { useOnboardingStore } from '../store/onboarding.store';
import { useBulletGenerator } from '../store/bullet-store';

import { setAuthState } from '../composables/auth';
import { markCoverLetterPassed } from '../api/profile';

const CoverLetterAiLogo = '/img/cover-letter-ai-logo.png';

// check if the user passed home onboarding
const { getUser, setUser } = setAuthState();
const passed = getUser()?.options?.coverletter_passed;
const visible = ref(!passed);

const onboardingStore = useOnboardingStore();
onboardingStore.markAsOnboarding(visible.value);

const bulletGenerator = useBulletGenerator();

const handleCloseModal = () => {
	visible.value = false;

	bulletGenerator.setOpened(false);
	onboardingStore.markAsOnboarding(false);
    
	markCoverLetterPassed().then(() => {
		setUser({
			...getUser(),
			options: { ...getUser()?.options, ...{ coverletter_passed: true } },
		});
	});
};

const handleStartOnboarding = async() => {
	// close modal
	visible.value = false;

	// Show the select resume modal BEFORE getting steps so elements exist in DOM
	onboardingStore.setOpenedCoverLetter(true);
	// Ensure modal starts in step 1 state (resume selection)
	onboardingStore.setCurrentStep('onboarding-select-resume');

	// Wait for DOM to update with the modal content
	await delay(300);

	const steps = coverletterOnboarding();

	const options = {
		// EN-1183: this tour drives the cover letter modal's step state itself (onbeforechange
		// below), so clicks inside it would desync the walkthrough. Esc and an overlay click are the
		// way out; onExit resets that state.
		disableInteraction: true,
		showBullets: true,
		exitOnOverlayClick: true,
		exitOnEsc: true,
		prevLabel: 'Previous',
		nextLabel: 'Next',
		doneLabel: 'Finish',
		tooltipClass: 'onboarding-tooltip',
		buttonClass: introJsButton,
		steps,
	};

	const onExit = () => {
		onboardingStore.markAsOnboarding(false);
		onboardingStore.setOpenedCoverLetter(false);
		onboardingStore.setCurrentStep(null);

		markCoverLetterPassed().then(() => {
			setUser({
				...getUser(),
				options: { ...getUser()?.options, ...{ coverletter_passed: true } },
			});
		});
	};

	// smart hack for bullet point generator
	const currentStep = ref(null);

	const intro = introJS();

	intro
		.onexit(onExit)
		.oncomplete(onExit)
		.onchange((targetElement) => {
			onboardingStore.setCurrentStep(targetElement?.['id'] ?? null);
		})
		.onbeforechange(async function(targetElement) {
			// Get the step index we're going to (0-based)
			const nextStepIndex = this._currentStep;

			// Set the correct modal state BEFORE the step change
			switch (nextStepIndex) {
				case 0:
					// Step 1: Resume selection view
					onboardingStore.setCurrentStep('onboarding-select-resume');
					onboardingStore.setOpenedCoverLetter(true);
					break;
				case 1:
					// Step 2: Form view
					onboardingStore.setCurrentStep('onboarding-select-resume-step2');
					onboardingStore.setOpenedCoverLetter(true);
					break;
				case 2:
					// Step 3: Builder content
					onboardingStore.setOpenedCoverLetter(false);
					break;
			}

			// Wait for Vue to update the DOM
			await delay(150);

			// Re-query the element since it may have just been rendered
			const stepId = nextStepIndex === 0 ? 'onboarding-select-resume'
				: nextStepIndex === 1 ? 'onboarding-select-resume-step2'
					: null;

			if (stepId) {
				const element = document.querySelector(`#${stepId}`);
				if (element) {
					// Update the step's element reference
					this._introItems[nextStepIndex].element = element;
					element.scrollIntoView({
						block: 'center',
						inline: 'center',
					});
				}
			} else if (targetElement) {
				targetElement.scrollIntoView({
					block: 'center',
					inline: 'center',
				});
			}

			currentStep.value = nextStepIndex + 1;
		})
		.setOptions(options)
		.start();
};
</script>

<style scoped>
.page {
  background-color: #fff;
  margin: 40px auto 64px;
}
</style>
